import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHead from "@/app/PageHead";
import RatingWidget from "@/features/ratings/RatingWidget";
import { getRecipe } from "@/lib/catalog-client";
import { useRecipeRedirect } from "@/lib/redirects";
import type { RecipeDocument } from "@/lib/types";
import { pluginLabel } from "@/lib/types";

export default function RecipeDetailPage() {
  useRecipeRedirect();
  const { id = "" } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<RecipeDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedMermaid, setCopiedMermaid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setError(null);
    getRecipe(id)
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onCopyPrompt = useCallback(async () => {
    if (!doc) return;
    try {
      await navigator.clipboard.writeText(doc.prompt_text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }, [doc]);

  const onCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      /* ignore */
    }
  }, []);

  const onCopyMermaid = useCallback(async () => {
    if (!doc?.blueprint_mermaid) return;
    try {
      await navigator.clipboard.writeText(doc.blueprint_mermaid);
      setCopiedMermaid(true);
      window.setTimeout(() => setCopiedMermaid(false), 1500);
    } catch {
      setCopiedMermaid(false);
    }
  }, [doc]);

  if (error) {
    return (
      <section>
        <h1 className="text-2xl font-semibold">Recipe</h1>
        <p className="mt-2 text-red-700 dark:text-red-400" role="alert">
          {error.includes("404") ? "Recipe not found." : error}
        </p>
        <p className="mt-3">
          <Link to="/recipes" className="text-brand-600 hover:underline dark:text-brand-500">
            Back to recipes
          </Link>
        </p>
      </section>
    );
  }

  if (!doc) {
    return (
      <p className="text-slate-500" aria-live="polite">
        Loading…
      </p>
    );
  }

  return (
    <article className="space-y-6">
      <PageHead
        title={doc.title}
        description={doc.summary}
        canonical={typeof window !== "undefined" ? window.location.href : undefined}
        image={
          doc.screenshots[0]
            ? new URL(
                `/assets/${doc.id}/${doc.screenshots[0].src}`,
                typeof window !== "undefined" ? window.location.href : "http://localhost",
              ).toString()
            : undefined
        }
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: doc.title,
          description: doc.summary,
          dateModified: doc.last_verified_on,
          author: { "@type": "Person", name: doc.reviewed_by },
          step: doc.prompt_text.split(/\n\n+/).map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            text: s.trim(),
          })),
        }}
      />
      <header className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <p className="text-sm text-slate-500">
          <Link to="/recipes" className="hover:underline">
            ← Recipes
          </Link>
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">{doc.title}</h1>
        <p className="mt-2 text-lg text-slate-700 dark:text-slate-300">{doc.summary}</p>

        <ul className="mt-4 flex flex-wrap gap-2 text-xs">
          <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200 font-medium">
            {doc.generated_by === "copilot" ? "AI-generated" : "Human-authored"} · curated by{" "}
            <a
              href={`https://github.com/${doc.reviewed_by}`}
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              @{doc.reviewed_by}
            </a>
          </li>
          {doc.status === "verified" && doc.last_verified_on ? (
            <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200 font-medium">
              Verified {doc.last_verified_on}
              {doc.verified_against_cowork_build ? ` · Cowork ${doc.verified_against_cowork_build}` : ""}
            </li>
          ) : (
            <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 font-medium">
              Draft · not yet verified
            </li>
          )}
          <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100 font-medium">
            v{doc.version}
          </li>
          {doc.deprecated ? (
            <li className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200 font-medium">
              Deprecated
            </li>
          ) : null}
        </ul>
      </header>

      {doc.status === "draft" ? (
        <div
          role="alert"
          className="rounded-xl border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-900 dark:text-amber-200"
        >
          <strong className="font-semibold">Draft recipe — not yet verified.</strong>{" "}
          {doc.plugin === "none" ? (
            <>
              The prompt and skill content below are starter content. No one has executed this
              against a live Cowork tenant. Validate against your own environment before relying on
              it.
            </>
          ) : (
            <>
              The prompt, OOTB skill list, and {pluginLabel(doc.plugin)} plugin action ids below are
              starter content. No one has executed this against a live Cowork tenant. Plugin action
              ids may not match Microsoft&apos;s actual published surface. Validate against your own
              environment before relying on it.
            </>
          )}
        </div>
      ) : null}

      {doc.mutates_data ? (
        <div
          role="alert"
          className="rounded-xl border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-900 dark:text-amber-200"
        >
          <strong className="font-semibold">Heads up:</strong> this recipe creates or modifies Dynamics 365 data. Test in a
          sandbox environment before running in production.
        </div>
      ) : null}

      {doc.business_value ? (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            Business value
          </h2>
          <p className="mt-3 text-slate-700 dark:text-slate-300 leading-relaxed">
            {doc.business_value}
          </p>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
          About this recipe
        </h2>
        <div
          className="prose prose-slate dark:prose-invert max-w-none mt-3
            prose-headings:font-semibold prose-headings:text-slate-900 dark:prose-headings:text-slate-100
            prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2
            prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-1
            prose-p:text-slate-700 dark:prose-p:text-slate-300
            prose-li:text-slate-700 dark:prose-li:text-slate-300
            prose-strong:text-slate-900 dark:prose-strong:text-slate-100
            prose-code:text-pink-700 dark:prose-code:text-pink-300 prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
            prose-img:rounded-lg prose-img:border prose-img:border-slate-200 dark:prose-img:border-slate-700"
          // Sanitized at build time (rehype-sanitize) in tools/build-catalog.
          dangerouslySetInnerHTML={{ __html: doc.description_html }}
        />
      </section>

      {doc.blueprint ? (
        <section
          aria-labelledby="blueprint-h"
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm"
        >
          <h2
            id="blueprint-h"
            className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400"
          >
            Blueprint
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Paste this diagram into Cowork <strong>first</strong>, then paste the prompt below in
            the same message. The diagram carries the shape of the process; the prompt binds every
            input to a value.
          </p>

          {/* Blueprints are tall by nature, so cap the inline preview and let it open full size. */}
          <a
            href={`/assets/${doc.id}/${doc.blueprint.image}`}
            target="_blank"
            rel="noreferrer"
            className="mt-4 block rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-3 hover:border-brand-500 transition-colors"
          >
            <img
              src={`/assets/${doc.id}/${doc.blueprint.image}`}
              alt={`Workflow blueprint diagram for ${doc.title}`}
              loading="lazy"
              decoding="async"
              className="mx-auto w-full max-w-sm"
            />
          </a>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Preview is scaled to fit — open or download the diagram to read it at full size.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`/assets/${doc.id}/${doc.blueprint.image}`}
              download
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-brand-500 text-white hover:bg-brand-600 shadow-sm transition-colors"
            >
              Download diagram
            </a>
            {doc.blueprint_mermaid ? (
              <button
                type="button"
                onClick={onCopyMermaid}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-brand-500 transition-colors"
              >
                {copiedMermaid ? "Copied!" : "Copy Mermaid source"}
              </button>
            ) : null}
          </div>

          <h3 className="mt-6 text-sm font-semibold text-slate-900 dark:text-slate-100">
            Input bindings
          </h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Change these in the prompt to retarget the workflow.
          </p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th scope="col" className="py-2 pr-4 font-medium">Variable</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Value</th>
                  <th scope="col" className="py-2 font-medium">Meaning</th>
                </tr>
              </thead>
              <tbody>
                {doc.blueprint.inputs.map((inp) => (
                  <tr key={inp.name} className="border-t border-slate-200 dark:border-slate-800">
                    <td className="py-2 pr-4 align-top">
                      <code className="text-xs text-pink-700 dark:text-pink-300">{inp.name}</code>
                    </td>
                    <td className="py-2 pr-4 align-top text-slate-900 dark:text-slate-100">
                      {inp.value}
                    </td>
                    <td className="py-2 align-top text-slate-600 dark:text-slate-400">
                      {inp.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {typeof doc.blueprint.clarifying_questions_asked === "number" ? (
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
              During verification Cowork asked{" "}
              <strong className="text-slate-900 dark:text-slate-100">
                {doc.blueprint.clarifying_questions_asked}
              </strong>{" "}
              clarifying question
              {doc.blueprint.clarifying_questions_asked === 1 ? "" : "s"} before executing.
            </p>
          ) : null}
        </section>
      ) : null}

      <section
        aria-labelledby="prompt-h"
        className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm"
      >
        <h2
          id="prompt-h"
          className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400"
        >
          Prompt
        </h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Copy and paste this into Cowork to run the recipe.
        </p>
        <div className="mt-3 relative">
          <pre className="whitespace-pre-wrap break-words rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 p-4 pr-28 text-sm leading-relaxed font-mono">
            <code>{doc.prompt_text}</code>
          </pre>
          <button
            type="button"
            onClick={onCopyPrompt}
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-brand-500 text-white hover:bg-brand-600 shadow-sm transition-colors"
          >
            {copied ? "Copied!" : "Copy prompt"}
          </button>
        </div>
      </section>

      {doc.skill_html ? (
        <section
          aria-labelledby="skill-h"
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm"
        >
          <h2
            id="skill-h"
            className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400"
          >
            Skill · {doc.skill_meta?.name ?? "(unnamed)"}
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {doc.skill_meta?.description}
          </p>
          <div
            className="prose prose-slate dark:prose-invert max-w-none mt-3
              prose-headings:font-semibold prose-headings:text-slate-900 dark:prose-headings:text-slate-100
              prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2
              prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-1
              prose-p:text-slate-700 dark:prose-p:text-slate-300
              prose-li:text-slate-700 dark:prose-li:text-slate-300
              prose-strong:text-slate-900 dark:prose-strong:text-slate-100
              prose-code:text-pink-700 dark:prose-code:text-pink-300 prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none"
            dangerouslySetInnerHTML={{ __html: doc.skill_html }}
          />
          {doc.skill_companions && doc.skill_companions.length > 0 ? (
            <div className="mt-6 border-t border-slate-200 dark:border-slate-800 pt-6 space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
                Companion files ({doc.skill_companions.length})
              </h3>
              {doc.skill_companions.map((companion) =>
                companion.html ? (
                  <div
                    key={companion.path}
                    id={companion.anchor_id}
                    className="scroll-mt-24 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/60 p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                      <code className="text-xs font-semibold text-pink-700 dark:text-pink-300">
                        {companion.path}
                      </code>
                      <a
                        href={companion.asset_url}
                        download
                        className="text-xs font-medium text-brand-600 dark:text-brand-400 underline"
                      >
                        Download raw
                      </a>
                    </div>
                    <div
                      className="prose prose-slate dark:prose-invert max-w-none mt-3
                        prose-headings:font-semibold prose-headings:text-slate-900 dark:prose-headings:text-slate-100
                        prose-h2:text-lg prose-h2:mt-5 prose-h2:mb-2
                        prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1
                        prose-p:text-slate-700 dark:prose-p:text-slate-300
                        prose-li:text-slate-700 dark:prose-li:text-slate-300
                        prose-strong:text-slate-900 dark:prose-strong:text-slate-100
                        prose-code:text-pink-700 dark:prose-code:text-pink-300 prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none"
                      dangerouslySetInnerHTML={{ __html: companion.html }}
                    />
                  </div>
                ) : (
                  <div
                    key={companion.path}
                    id={companion.anchor_id}
                    className="scroll-mt-24 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/60 px-4 py-2.5"
                  >
                    <code className="text-xs font-semibold text-pink-700 dark:text-pink-300">
                      {companion.path}
                    </code>
                    <a
                      href={companion.asset_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-brand-600 dark:text-brand-400 underline"
                    >
                      Open / download
                    </a>
                  </div>
                ),
              )}
            </div>
          ) : null}
          {doc.skill_zip?.available ? (
            <p className="mt-4 text-sm">
              <a
                href={`/assets/${doc.id}/skill.zip`}
                className="text-brand-600 dark:text-brand-400 underline font-medium"
                download
              >
                Download skill package
              </a>{" "}
              <span className="text-slate-500">
                — SHA-256: <code className="text-xs">{doc.skill_zip.sha256}</code>
              </span>
            </p>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              No packaged <code className="text-xs px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800">skill.zip</code> yet. Build the zip via the recipes-repo CI.
            </p>
          )}
        </section>
      ) : null}

      <section
        aria-labelledby="rating-h"
        className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm"
      >
        <h2
          id="rating-h"
          className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400"
        >
          Rate this recipe
        </h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Anonymous, one rating per day.
        </p>
        <div className="mt-3">
          <RatingWidget recipeId={doc.id} />
        </div>
      </section>

      <section
        aria-labelledby="skills-used-h"
        className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm"
      >
        <h2
          id="skills-used-h"
          className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400"
        >
          Skills used
        </h2>
        <ul className="mt-3 flex flex-wrap gap-2 text-xs">
          {doc.uses_skills.ootb.map((s) => (
            <li
              key={`o-${s}`}
              className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-900 dark:bg-blue-950/60 dark:text-blue-200 font-medium"
            >
              OOTB · {s}
            </li>
          ))}
          {doc.uses_skills.plugin.map((p) => (
            <li
              key={`p-${p.plugin}/${p.action}`}
              className="px-2.5 py-1 rounded-full bg-purple-100 text-purple-900 dark:bg-purple-950/60 dark:text-purple-200 font-medium"
            >
              Plugin · {p.plugin}/{p.action}
            </li>
          ))}
          {doc.uses_skills.custom.map((c) => (
            <li
              key={`c-${c}`}
              className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 font-medium"
            >
              Custom · {c}
            </li>
          ))}
        </ul>
      </section>

      <footer className="border-t border-slate-200 dark:border-slate-800 pt-4 text-sm flex flex-wrap gap-4 text-slate-600 dark:text-slate-400">
        <button type="button" onClick={onCopyLink} className="hover:underline">
          Copy link
        </button>
        <a
          href={`https://github.com/seangalliher/Coworkcookbook/tree/main/recipes/${doc.slug}`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
        >
          View source on GitHub
        </a>
        <a
          href={`https://github.com/seangalliher/Coworkcookbook/issues/new?title=${encodeURIComponent(
            `[Broken] ${doc.id}`,
          )}&body=${encodeURIComponent(
            `Recipe: ${doc.id}\nURL: ${typeof window !== "undefined" ? window.location.href : ""}`,
          )}&labels=bug,recipe-broken`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
        >
          Report an issue
        </a>
      </footer>
    </article>
  );
}
